/*
 * frame-addon.js - Track E, E1 step 3.
 * LIVE 2026-08-18. Updated 2026-08-18 (v3): flat dropdown, per-option price,
 * a "Totaal incl. lijst" line. Updated 2026-08-19 (v4): dropdown options
 * read "{Material} - {Color}", and the frame's price is written into the
 * theme's own price display instead of a separate line. STAGED, not yet
 * pushed, 2026-08-27: publishes `walljar_frame_addon_added` (analysis/
 * tracking/event-spec-v1.md §4) via Shopify's custom-pixel analytics API
 * once the two/three-line /cart/add.js call actually succeeds - see
 * publishFrameAddonAdded and its one call site.
 *
 * Sells a frame as a separate cart line instead of as the `Poster met lijst`
 * variant. Written against the theme rather than around it:
 *
 *   - The style picker is a flat <select> - not grouped into <optgroup>s
 *     (v2 had material-family groups; removed per feedback) and not a
 *     button grid (14 styles is a lot of buttons). Frames are still walked
 *     via groupFramesByKind()'s fixed wood/aluminium_mat/aluminium_glans
 *     order rather than raw price/family sort, so the *order* options
 *     appear in is still sensible even with no group headings shown. Each
 *     <option>'s label is "{material} - {color}" (frame_map.py now carries
 *     both explicitly, split from the old flat frame_family string) rather
 *     than the flat family string itself.
 *   - Every frame carries its own price_cents (frame_map.py writes it per
 *     entry) - each <option> shows it as a signed diff from the base print
 *     price, "(+ X,XX)"/"(- X,XX)"/"(X,XX)" for zero (priceDiffText, no
 *     currency symbol - matches formatMoney's own convention, every other
 *     price on the page is unmarked too). v2 showed one price for the whole
 *     dropdown, which only worked because every style happened to be priced
 *     identically; per-option pricing is what's future-proof if that ever
 *     changes.
 *   - v4 writes the selected frame's price into the theme's own price-list
 *     element (writeMergedPrice()) instead of v3's separate "Totaal incl.
 *     lijst" line ([data-frame-addon-price], retired). v3 avoided that
 *     element deliberately because the theme's own _updateProductPrices
 *     fully rebuilds it on every variant change (assets/theme.js:3248) - but
 *     _onVariantChanged runs _updateProductPrices as its first step and only
 *     dispatches `variant:changed` as its last, synchronous step
 *     (assets/theme.js:3205-3242), so this add-on's own `variant:changed`
 *     handler always runs after the theme's rebuild, never races it.
 *     Selecting a frame (which fires no `variant:changed` at all - see
 *     below) never collides with a rebuild in the first place.
 *   - Every cart line this file adds carries `_wj_set` (a token shared by
 *     the print/frame/service trio) and `_wj_role` ('print'/'frame'/
 *     'service') as hidden (underscore-prefixed) properties.
 *     patches/theme/frame-addon/assets/frame-addon-cart.js reads both,
 *     cart-side, to keep the trio's quantities linked and cascade removal
 *     (remove print -> frame+service go too; remove frame -> service goes
 *     too; remove service -> nothing else changes). See
 *     analysis/framing/DECISIONS.md's dated entry for the full reasoning -
 *     this file only ever *creates* the properties, never reads them back.
 *
 *   - Size and material come from the theme's own `variant:changed` event
 *     (assets/theme.js:3231). Variant resolution is NOT reimplemented.
 *   - The add-to-cart path is only intercepted when a frame is actually
 *     selected. With no frame chosen the click passes straight through to the
 *     theme's `_addToCart` (assets/theme.js:3579) untouched, so the default
 *     path stays 100% existing theme code. That is the point of making the
 *     intercept conditional rather than total.
 *   - Interception is capture-phase on document. The theme's Delegate uses
 *     capture only for non-bubbling events (assets/theme.js:474-480), so click
 *     handlers run in the bubble phase and this is deterministic, not a race.
 *
 * Why a second /cart/add.js call is not an option: the theme posts
 * Form.serialize(form) (assets/theme.js:3003), which turns `items[0][id]` into
 * an object keyed "0", not an array. Shopify needs an array. So the add has
 * to be issued here, as one atomic request - two lines (print, frame), or
 * three when the generated map carries a service_variant_id (print, frame,
 * the free framing-service line).
 *
 * ES5 on purpose - the theme's bundle is Babel output targeting the same range.
 */
(function () {
  'use strict';

  if (window.__walljarFrameAddon) { return; }
  window.__walljarFrameAddon = true;

  var SECTION_SELECTOR = '[data-section-type="product"]';

  /* ------------------------------------------------------------------ money */

  /* window.theme.moneyFormat on this shop is "{{amount_with_comma_separator}}"
   * - no currency symbol - so prices render as "17,99", matching every other
   * price on the page. Implemented here because the theme's Currency class
   * lives inside its IIFE and is not exposed on window. */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '{{amount}}';
    var amount = Math.round(cents);

    function withDelimiters(precision, thousands, decimal) {
      var fixed = (amount / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(2, "'", '.');
        default:
          return withDelimiters(2, ',', '.');
      }
    });
  }

  /* Each <option>'s own price, as a signed difference from the base print
   * price: "(+ X,XX)" for a frame that costs more, "(- X,XX)" for one that
   * costs less (frame_map.py has never produced a negative price_cents, but
   * per-option pricing exists so a future one could without a code change -
   * this is the future-proofing that motivated per-option pricing in the
   * first place). Zero (the "no frame" option) gets no sign - it is the
   * baseline being compared against, not a change from it. */
  function priceDiffText(cents) {
    if (!cents) { return '(' + formatMoney(0) + ')'; }
    var sign = cents < 0 ? '- ' : '+ ';
    return '(' + sign + formatMoney(Math.abs(cents)) + ')';
  }

  /* -------------------------------------------------------------------- map */

  function normalise(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* frame_map.py writes every size key as MIN x MAX ("50 x 70 cm"), never the
   * reverse - but a variant's own size option value is catalogue data, not
   * generated, and this store's `orientation` facet (2 values, 3,673
   * tag-product pairs) shows portrait/landscape is a real axis here. Today no
   * option value is ever written in the reversed order (checked against the
   * full catalogue export), but framesFor() matches by exact string, so a
   * future or undiscovered "70 x 50 cm" would silently render no add-on
   * rather than the frame that actually fits - the same failure mode as an
   * unmapped single-variant handle. Registering both orderings here means
   * framesFor()'s lookup needs no change and every call site is covered. */
  function reversedSizeKey(value) {
    var m = /^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm$/.exec(value);
    if (!m) { return null; }        // leaves "…cm (A1)"-style entries alone
    return m[2] + ' x ' + m[1] + ' cm';
  }

  function readMap(section) {
    var element = section.querySelector('.js-frame-map');
    if (!element) { return null; }
    try {
      var map = JSON.parse(element.textContent);
      if (!map || !map.sizes) { return null; }
      map._index = {};
      map._materials = [];
      var sizes = Object.keys(map.sizes);
      for (var i = 0; i < sizes.length; i++) {
        var key = normalise(sizes[i]);
        map._index[key] = map.sizes[sizes[i]];
        var reversed = reversedSizeKey(key);
        if (reversed && reversed !== key) { map._index[reversed] = map.sizes[sizes[i]]; }
      }
      var materials = map.eligible_materials || [];
      for (var j = 0; j < materials.length; j++) {
        map._materials.push(normalise(materials[j]));
      }
      return map;
    } catch (error) {
      /* An inert JSON block that fails to parse costs the add-on and nothing
       * else. This theme has form here - D4 defect 1 was an inline <script>
       * that threw on every page load for years. */
      return null;
    }
  }

  /* Which frames fit the selected variant.
   *
   * Matched on option VALUES, never on option names. The size option is called
   * `Maat`, `Formaat`, `Size` and "In welk formaat zou je het willen hebben?"
   * across this catalogue (analysis/taxonomy/option-map.csv), and B1's rename
   * is not part of this release. Values are canonical; names are not.
   *
   * Returns null - meaning render nothing at all - when the material is not
   * eligible or the size has no buyable frame. The 11 canonical sizes in the
   * ~8.7% revenue tail hit the second case, and they must degrade quietly. */
  /* 2026-08-22 stopgap: a tweeluik/drieluik/matching-duo product is one SKU
   * covering 2-3 physical prints, and until buildCartItems' quantity logic
   * below is panel-count-aware, adding a frame to one of these sends
   * exactly one frame/service line no matter how many panels the customer
   * is actually framing - a real underbilling bug, not a display glitch.
   * frame_map.py's suppressed_handles lists every bundle product
   * unconditionally; this renders nothing at all for them, the same quiet
   * degrade already used for a size with no buyable frame. Checked first,
   * before any material/size matching, so a bundle product never gets a
   * momentary "buyable" flash before falling back. */
  function isSuppressed(map, handle) {
    return !!(map && map.suppressed_handles && handle &&
              map.suppressed_handles[handle]);
  }

  /* How many frame/service lines one unit of this print actually needs -
   * frame_map.py's item_counts, from the live custom.item_count metafield.
   * 1 for every ordinary product (the metafield is absent, not zero, for
   * anything that has never needed this - see setup_item_count_metafield.py).
   * Called from onAddToCartCapture below, but only ever returns something
   * other than 1 for a handle in suppressed_handles - isSuppressed keeps
   * those from reaching the add-on at all today (2026-08-22 stopgap), so
   * this is effectively inert in production until that map data changes,
   * not until this code does. */
  function itemCountFor(map, handle) {
    var n = map && map.item_counts && handle && map.item_counts[handle];
    return (n && n > 0) ? n : 1;
  }

  /* The size string matched to the base variant - same option1/2/3 scan
   * framesFor uses to find frames, pulled out separately because the
   * walljar_frame_addon_added pixel event (analysis/tracking/event-spec-
   * v1.md §4) needs it and framesFor itself doesn't return it. */
  function sizeFor(map, variant) {
    if (!map || !variant) { return ''; }
    var values = [variant.option1, variant.option2, variant.option3];
    for (var i = 0; i < values.length; i++) {
      var value = normalise(values[i]);
      if (value && map._index[value]) { return values[i]; }
    }
    return '';
  }

  /* Reads Shopify's own `cart` cookie rather than an extra /cart.js round
   * trip - the ajax cart API's /cart/add.js response carries the added
   * line, not the cart's token, and this event fires right after that
   * call resolves. */
  function currentCartToken() {
    var match = document.cookie.match(/(?:^|; )cart=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  /* walljar_frame_addon_added - analysis/tracking/event-spec-v1.md §4.
   * Fires only on the branch where the two-line (or three-line, with the
   * service line) /cart/add.js call actually succeeded - see the
   * `response.ok` check at this function's one call site. Degrades
   * quietly if the pixel API isn't present (same posture as the missing-
   * service-id case above), not a page error. */
  function publishFrameAddonAdded(productId, printId, family, size, priceCents, associationProperty) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }
    window.Shopify.analytics.publish('walljar_frame_addon_added', {
      product_id: productId,
      variant_id: Number(printId),
      frame_material: family || '',
      frame_size: size || '',
      frame_price_cents: priceCents || 0,
      association_property: associationProperty,
      cart_token: currentCartToken()
    });
  }

  function framesFor(map, variant, handle) {
    if (!map || !variant) { return null; }
    if (isSuppressed(map, handle)) { return null; }
    var values = [variant.option1, variant.option2, variant.option3];
    var materialOk = false;
    var frames = null;

    for (var i = 0; i < values.length; i++) {
      var value = normalise(values[i]);
      if (!value) { continue; }
      if (map._materials.indexOf(value) !== -1) { materialOk = true; }
      if (map._index[value]) { frames = map._index[value]; }
    }

    /* Products whose size is a product fact, not an option (the eigen-ontwerp
     * range). Their single variant is `Default Title`, so the loop above
     * resolves neither size nor material and every one of them would render
     * nothing - which after E1's fold would delete the custom framing offer
     * instead of migrating it.
     *
     * The handle is LOOKED UP in the generated map, never parsed. An unknown
     * handle stays null and renders nothing, so the failure mode is a missing
     * add-on rather than a frame that does not fit. Material is implied: the
     * generator only lists handles it resolved to a size with buyable frames.
     *
     * Checked last so a real option always wins. If one of these products ever
     * grows a genuine size option, the loop above answers first and the
     * generator drops it from "products" on the next run. */
    if (!frames && handle && map.products) {
      var mapped = map.products[handle];
      if (mapped) {
        var byMapped = map._index[normalise(mapped)];
        if (byMapped) {
          frames = byMapped;
          materialOk = true;
        }
      }
    }

    return (materialOk && frames && frames.length) ? frames : null;
  }

  /* ------------------------------------------------------------- theme state */

  function productData(section) {
    var element = section.querySelector('[data-product-json]');
    if (!element) { return null; }
    try {
      return JSON.parse(element.innerHTML);
    } catch (error) {
      return null;
    }
  }

  function variantById(data, id) {
    if (!data || !data.product || !data.product.variants) { return null; }
    for (var i = 0; i < data.product.variants.length; i++) {
      if (String(data.product.variants[i].id) === String(id)) {
        return data.product.variants[i];
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ render */

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function setStatus(addon, message) {
    var status = addon.querySelector('[data-frame-addon-status]');
    if (status) { status.textContent = message || ''; }
  }

  /* The three print.com style families, in a fixed customer-facing order
   * (matches create_frame_products.py's FRAME_STYLES grouping) - NOT the
   * order frames happen to arrive in, which is sorted (price_cents, family)
   * by frame_map.py and would otherwise put "Aluminium glans" first purely
   * because it sorts alphabetically before "Hout". Any kind not in this
   * list (there is none today, but frame_map.py falls back to "unknown"
   * for an unrecognised Stijl value) is appended after, so nothing is ever
   * silently dropped from the dropdown - it just lands at the end. No
   * customer-facing label is attached to a kind any more (v3 dropped the
   * <optgroup> headings) - this only ever controls display ORDER now. */
  var GROUP_ORDER = ['wood', 'aluminium_mat', 'aluminium_glans'];

  /* Pure - pulled out for scripts/theme/test_frame_addon.js, same reasoning
   * as buildCartItems. Groups frames by `kind` and orders the groups by
   * GROUP_ORDER first, then any unrecognised kind in first-seen order. */
  function groupFramesByKind(frames) {
    var groups = {};
    var seen = [];
    for (var i = 0; i < frames.length; i++) {
      var kind = frames[i].kind || '';
      if (!groups[kind]) { groups[kind] = []; seen.push(kind); }
      groups[kind].push(frames[i]);
    }
    var order = [];
    for (var k = 0; k < GROUP_ORDER.length; k++) {
      if (groups[GROUP_ORDER[k]]) { order.push(GROUP_ORDER[k]); }
    }
    for (var j = 0; j < seen.length; j++) {
      if (order.indexOf(seen[j]) === -1) { order.push(seen[j]); }
    }
    return { groups: groups, order: order };
  }

  /* Writes base print price + selected frame price into the theme's own
   * .price-list (see header comment for why this is safe against the
   * theme's own rebuild). addon._basePriceCents is cached by update() from
   * the currently selected variant. Called with frameCents 0 when no frame
   * is selected, which restores .price-list to the plain base price - not a
   * no-op, since a previous call may have written a frame-inclusive total
   * into it.
   *
   * Finds the sale-price <span> (".price-list .price:not(.price--compare)")
   * and rebuilds everything after its nested .visually-hidden label span,
   * matching the markup _updateProductPrices itself writes
   * (product-variant-selector.liquid:180-188). The struck-through compare-at
   * price, if shown, is deliberately left untouched - the frame is added to
   * the sale price only. */
  function writeMergedPrice(addon, frameCents) {
    var section = addon.closest(SECTION_SELECTOR);
    var priceEl = section && section.querySelector('.price-list .price:not(.price--compare)');
    if (!priceEl) { return; }
    var hidden = priceEl.querySelector('.visually-hidden');
    var node = hidden ? hidden.nextSibling : priceEl.firstChild;
    while (node) {
      var next = node.nextSibling;
      priceEl.removeChild(node);
      node = next;
    }
    var base = addon._basePriceCents || 0;
    priceEl.appendChild(document.createTextNode(formatMoney(base + (frameCents || 0))));
  }

  function updateSelectionAttrs(addon, variantId, family, priceCents) {
    if (variantId) {
      addon.setAttribute('data-frame-addon-variant', variantId);
      addon.setAttribute('data-frame-addon-family', family || '');
      addon.setAttribute('data-frame-addon-price-cents', priceCents || 0);
      writeMergedPrice(addon, priceCents || 0);
    } else {
      addon.removeAttribute('data-frame-addon-variant');
      addon.removeAttribute('data-frame-addon-family');
      addon.removeAttribute('data-frame-addon-price-cents');
      writeMergedPrice(addon, 0);
    }
    setStatus(addon, '');
  }

  function render(addon, frames) {
    var select = addon.querySelector('[data-frame-addon-select]');
    if (!select) { return; }

    if (!frames) {
      select.innerHTML = '';
      addon.hidden = true;
      updateSelectionAttrs(addon, '', '', 0);
      return;
    }

    /* Carry the chosen family across a size change where the same family is
     * still buyable. Changing 50x70 to 60x90 should not silently drop the oak
     * frame the customer already picked. */
    var wanted = addon.getAttribute('data-frame-addon-family') || '';

    var grouped = groupFramesByKind(frames);
    var html = ['<option value="" data-frame-price-cents="0">' +
                escapeHtml(addon.getAttribute('data-frame-addon-none') || '') +
                ' ' + escapeHtml(priceDiffText(0)) + '</option>'];

    for (var g = 0; g < grouped.order.length; g++) {
      var entries = grouped.groups[grouped.order[g]];
      for (var i = 0; i < entries.length; i++) {
        var frame = entries[i];
        /* "{material} - {color}", e.g. "Aluminium glans - Champagne" - not
         * the flat frame_family string ("Aluminium glans Champagne").
         * data-frame-family stays keyed on the flat string: it drives
         * size-change carry-over (render()'s `wanted` lookup, above) and the
         * `Lijst` cart line property, both unrelated to display format. */
        html.push(
          '<option value="' + escapeHtml(frame.variant_id) + '" ' +
          'data-frame-family="' + escapeHtml(frame.family) + '" ' +
          'data-frame-price-cents="' + escapeHtml(frame.price_cents) + '">' +
          escapeHtml(frame.material) + ' - ' + escapeHtml(frame.color) + ' ' +
          escapeHtml(priceDiffText(frame.price_cents)) +
          '</option>'
        );
      }
    }

    select.innerHTML = html.join('');
    addon.hidden = false;

    var matchIndex = 0;
    if (wanted) {
      for (var o = 1; o < select.options.length; o++) {
        if (select.options[o].getAttribute('data-frame-family') === wanted) { matchIndex = o; break; }
      }
    }
    select.selectedIndex = matchIndex;
    var chosen = select.options[matchIndex];
    updateSelectionAttrs(addon, chosen ? chosen.value : '',
                         chosen ? (chosen.getAttribute('data-frame-family') || '') : '',
                         chosen ? parseInt(chosen.getAttribute('data-frame-price-cents'), 10) || 0 : 0);
  }

  function update(section, variant) {
    var addon = section.querySelector('[data-frame-addon]');
    if (!addon) { return; }
    var map = addon._frameMap || (addon._frameMap = readMap(section));
    if (variant) { section._frameAddonVariantId = variant.id; }
    addon._basePriceCents = variant ? variant.price : 0;
    render(addon, framesFor(map, variant,
                            addon.getAttribute('data-frame-addon-handle')));
  }

  function setup(section) {
    if (section._frameAddonReady) { return; }
    var addon = section.querySelector('[data-frame-addon]');
    if (!addon) { return; }
    section._frameAddonReady = true;

    var data = productData(section);
    if (!data) { return; }
    update(section, variantById(data, data.selected_variant_id));
  }

  function scan(root) {
    var addons = (root || document).querySelectorAll('[data-frame-addon]');
    for (var i = 0; i < addons.length; i++) {
      var section = addons[i].closest(SECTION_SELECTOR);
      if (section) { setup(section); }
    }
  }

  /* --------------------------------------------------------------- add to cart */

  /* Line item properties already on the form - an app may have put them there.
   * Collected rather than assumed absent, so the add-on does not quietly drop
   * someone else's data on its way past. */
  function formProperties(form) {
    var properties = {};
    var fields = form.querySelectorAll('[name^="properties["]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.disabled) { continue; }
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) { continue; }
      var match = field.name.match(/^properties\[(.*)\]$/);
      if (match) { properties[match[1]] = field.value; }
    }
    return properties;
  }

  function clone(source) {
    var copy = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) { copy[key] = source[key]; }
    }
    return copy;
  }

  /* Pulled out as its own pure function so scripts/theme/test_frame_addon.js
   * can cover the third-line-item behaviour without stubbing DOM/fetch -
   * onAddToCartCapture itself is not unit-tested (it needs a real click
   * event, form and section), but the item list it hands to postCart() is a
   * plain array-building decision and belongs in something testable.
   *
   * Returned in REVERSE of the logical print/frame/service order. Confirmed
   * live 2026-08-18 against the real store: a single /cart/add.js call with
   * a multi-item `items` array is stored by Shopify in the REVERSE of the
   * order it was sent - send [print, frame, service] and the resulting cart
   * lists service, frame, print. Sending pre-reversed is what makes the
   * cart actually display print, frame, service, which is the order that
   * was asked for. Verified with two separate real /cart/add.js calls
   * against an anonymous test session, including a second add on top of an
   * already-non-empty cart, so this isn't a fresh-cart-only quirk. */
  function buildCartItems(printItem, frameItem, serviceItem) {
    var items = [frameItem, printItem];
    if (serviceItem) { items.unshift(serviceItem); }
    return items;
  }

  function postCart(items) {
    return fetch(window.routes.cartAddUrl + '.js', {
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  }

  function onAddToCartCapture(event) {
    var target = event.target;
    if (!target || !target.closest) { return; }

    var button = target.closest('[data-action="add-to-cart"]');
    if (!button) { return; }

    var section = button.closest(SECTION_SELECTOR);
    if (!section) { return; }

    var addon = section.querySelector('[data-frame-addon]');
    if (!addon || addon.hidden) { return; }

    var frameId = addon.getAttribute('data-frame-addon-variant');
    if (!frameId) { return; }   /* no frame chosen - the theme's own path runs */

    /* The free framing-service line, a flat EUR 0 product (briefly EUR 17.99
     * plus a 100%-off discount instead, reverted the same day - see
     * analysis/framing/DECISIONS.md's updates). The id travels in the same
     * generated map as the frame variant ids, under service_variant_id, set
     * by frame_map.py once the service product exists. Missing id degrades
     * quietly (print + frame only, no third line) rather than blocking the
     * add - the same posture as a size with no buyable frame. */
    var frameMap = addon._frameMap;
    var serviceId = frameMap && frameMap.service_variant_id;

    var form = section.querySelector('form[action*="/cart/add"]');
    if (!form) { return; }

    var printId = section._frameAddonVariantId;
    if (!printId) {
      var idField = form.querySelector('[name="id"]');
      printId = idField && idField.value;
    }
    if (!printId) { return; }   /* cannot identify the print - do not interfere */

    event.preventDefault();
    event.stopPropagation();

    var quantityField = form.querySelector('[name="quantity"]');
    var quantity = Math.max(1, parseInt(quantityField && quantityField.value, 10) || 1);
    var family = addon.getAttribute('data-frame-addon-family') || '';
    var printTitle = addon.getAttribute('data-frame-addon-product') || '';
    var token = 'wj-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

    /* A bundle product (tweeluik/drieluik/matching-duo) is one print-side
     * SKU covering itemCount physical panels, each needing its own frame -
     * frameQuantity, not quantity, is what the frame/service lines need.
     * itemCount travels on the PRINT line as _wj_item_count so
     * frame-addon-cart.js's computeCartCorrections can keep the ratio
     * (not just equality) correct after the initial add, with no need to
     * load frame_map.py's data on the cart page at all. 1 for every
     * ordinary product - isSuppressed already keeps a bundle product from
     * reaching this far while the 2026-08-22 stopgap is in effect (see its
     * own comment), so this is currently only ever 1 in practice, tested
     * ahead of that switch rather than after it. */
    var itemCount = itemCountFor(frameMap, addon.getAttribute('data-frame-addon-handle'));
    var frameQuantity = quantity * itemCount;

    var base = formProperties(form);

    var printProperties = clone(base);
    printProperties['Lijst'] = family;
    printProperties['_wj_set'] = token;
    printProperties['_wj_role'] = 'print';
    printProperties['_wj_item_count'] = String(itemCount);

    var frameProperties = {};
    frameProperties['Voor'] = printTitle;
    frameProperties['_wj_set'] = token;
    frameProperties['_wj_role'] = 'frame';

    var printItem = { id: Number(printId), quantity: quantity, properties: printProperties };
    var frameItem = { id: Number(frameId), quantity: frameQuantity, properties: frameProperties };

    var serviceItem = null;
    if (serviceId) {
      var serviceProperties = {};
      serviceProperties['Voor'] = printTitle;
      serviceProperties['_wj_set'] = token;
      serviceProperties['_wj_role'] = 'service';
      serviceItem = { id: Number(serviceId), quantity: frameQuantity, properties: serviceProperties };
    }
    var items = buildCartItems(printItem, frameItem, serviceItem);

    button.setAttribute('disabled', 'disabled');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var data = productData(section);
    var variant = variantById(data, printId);
    var frameSize = sizeFor(frameMap, variant);
    var framePriceCents = parseInt(addon.getAttribute('data-frame-addon-price-cents'), 10) || 0;
    var baseProductId = data && data.product ? data.product.id : null;

    function finish(addedLines) {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.removeAttribute('disabled');

      if (window.theme.cartType === 'page') {
        window.location.href = window.routes.cartUrl;
        return;
      }

      /* Hand back to the theme's own post-add path - mini-cart re-render, drawer
       * open, item count re-read from the server (assets/theme.js:1994). */
      section.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: {
          variant: variant,
          button: button,
          quantity: quantity * addedLines
        }
      }));
    }

    postCart(items).then(function (response) {
      if (response.ok) {
        publishFrameAddonAdded(baseProductId, printId, family, frameSize, framePriceCents, token);
        finish(items.length);
        return;
      }
      /* The frame went unbuyable between render and click. A sale is not lost
       * to a frame problem: add the print alone and say what happened. */
      return postCart([printItem]).then(function (retry) {
        setStatus(addon, addon.getAttribute('data-frame-addon-error') || '');
        if (retry.ok) { finish(1); }
        else { document.dispatchEvent(new CustomEvent('theme:loading:end')); button.removeAttribute('disabled'); }
      });
    }).catch(function () {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.removeAttribute('disabled');
      setStatus(addon, addon.getAttribute('data-frame-addon-error') || '');
    });
  }

  /* ------------------------------------------------------------------ wiring */

  document.addEventListener('click', onAddToCartCapture, true);

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-frame-addon-select')) { return; }
    var addon = target.closest && target.closest('[data-frame-addon]');
    if (!addon) { return; }
    var chosen = target.options[target.selectedIndex];
    updateSelectionAttrs(addon, target.value,
                         chosen ? (chosen.getAttribute('data-frame-family') || '') : '',
                         chosen ? parseInt(chosen.getAttribute('data-frame-price-cents'), 10) || 0 : 0);
  });

  /* The theme tells us the variant changed; we never work it out ourselves. */
  document.addEventListener('variant:changed', function (event) {
    var target = event.target;
    var section = (target && target.closest) ? target.closest(SECTION_SELECTOR) : null;
    if (!section) { return; }
    section._frameAddonReady = true;
    update(section, event.detail && event.detail.variant);
  });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  /* Quick view injects a whole product section after load, and there is no
   * theme event for it. Cheap enough: only added nodes are inspected. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var node = records[i].addedNodes[j];
          if (node.nodeType !== 1) { continue; }
          if (node.querySelector && node.querySelector('[data-frame-addon]')) { scan(node); }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
}());
